import { FC, createContext, useCallback, useContext, useEffect, useState } from "react";

const THEMES = ["light","dark"] as const;
type Theme = (typeof THEMES)[number];

type ThemeContextType = {
  theme: Theme;
  toggleTheme: () => void;
};

const ThemeContext = createContext<ThemeContextType | null>(null);

export const ThemeProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  const [themeIndex, setThemeIndex] = useState(0);

  useEffect(() => {
    const current = THEMES[themeIndex];
    THEMES.forEach((t) => document.documentElement.classList.remove(t));
    document.documentElement.classList.add(current);
  }, [themeIndex]);

  const toggleTheme = useCallback(() => {
    try {
      setThemeIndex((prev) => {
        const next = (prev + 1) % THEMES.length;
        if (!THEMES[next]) throw new Error("Invalid theme index");
        return next;
      });
    } catch {
      setThemeIndex(0);
    }
  }, []);

  return (
    <ThemeContext.Provider value={{ theme: THEMES[themeIndex], toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return ctx;
}
